'use client'

import Link from 'next/link'
import { ProductListItem } from '@/lib/api'
import { formatPrice, conditionLabel } from '@/lib/format'

const LOW_STOCK_THRESHOLD = 5

export function ProductCard({ product }: { product: ProductListItem }) {
  const primaryImage = product.images?.[0]?.url ?? null
  const soldOut = product.is_sold_out
  const lowStock =
    !soldOut && product.total_stock > 0 && product.total_stock <= LOW_STOCK_THRESHOLD

  return (
    <Link
      href={`/product/${product.slug}`}
      className="group flex flex-col rounded-2xl border border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-square bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        {primaryImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={primaryImage}
            alt={product.title}
            className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
              soldOut ? 'opacity-60 grayscale' : ''
            }`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300 dark:text-neutral-600">
            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          </div>
        )}

        {/* Sold out overlay */}
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="px-4 py-1.5 rounded-full bg-neutral-900/90 text-white text-sm font-semibold tracking-wide">
              Slutsåld
            </span>
          </div>
        )}

        {/* Category badge */}
        {product.category && !soldOut && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 dark:bg-neutral-900/90 text-[11px] font-medium text-neutral-700 dark:text-neutral-200 backdrop-blur">
            {product.category.name}
          </span>
        )}

        {/* Low stock badge */}
        {lowStock && (
          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[11px] font-semibold">
            Endast {product.total_stock} kvar
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-neutral-900 dark:text-white line-clamp-1">
            {product.title}
          </h3>
        </div>
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          {conditionLabel(product.condition)}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-lg font-semibold text-neutral-900 dark:text-white">
            {formatPrice(product.price)}
          </span>
          {soldOut ? (
            <span className="text-xs font-medium text-neutral-400">Ej i lager</span>
          ) : (
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              I lager
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}
